import React from 'react';
import classes from './MembersOfRole.module.scss';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { Avatar, Button, Dropdown, Empty, Input, MenuProps, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import Assets from 'assets/icons';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useDebounce } from 'use-debounce';
import Mark from 'mark.js';
import { setIsInviteUserOpen } from 'store/slices/windowStateSlice';
import cn from 'classnames';
import { setIsConfirmBlockOpen, setIsConfirmDeleteOpen, setProcessedUsers, setUserChoosen } from 'store/slices/dataUsersSlice';
import ConfirmDeleteUser from 'components/Modals/ConfirmDeleteUser/ConfirmDeleteUser';
import { changeBlockStatus, deleteInvite, deleteUserHandle, reInviteHandle } from 'store/reducers/usersGroupCreator';
import Spinner from 'components/Spinner/Spinner';
import ConfirmChangeStatus from 'components/Modals/ConfirmChangeStatus/ConfirmChangeStatus';

const MembersOfRole = () => {
  const [searchText, setSearchText] = React.useState<string>('');
  const [debouncedText] = useDebounce(searchText, 300);

  const markerRef = React.useRef<HTMLDivElement>(null);
  
  const { users, configurableRole, pendingInviteUsers, selectedUser, userWithIdProcessed,
    isConfirmDeleteOpen, isConfirmBlockOpen } = useAppSelector((state) => state.dataUsers);
  const { userPermissions } = useAppSelector((store) => store.userData);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  
  const isAvailableExcludeUser = userPermissions?.can_exclude_users;
  const isAvailableInviteUser = userPermissions?.can_invite_users;  

  const dispatch = useAppDispatch();

  const { t } = useTranslation();
  const filteredUsers = users.filter(user => {
    return user.group[0] === configurableRole?.group_id && (
      user.last_name.toLocaleLowerCase().includes(debouncedText.toLocaleLowerCase())
      || user.first_name.toLocaleLowerCase().includes(debouncedText.toLocaleLowerCase())
      || user.email.toLocaleLowerCase().includes(debouncedText.toLocaleLowerCase())
    )
  }).sort((a, b) => {
      if (a?.id === dataRoom?.owner_id) return -1;
      else { 
        return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
      }
    });
  const filteredInviteUsers = pendingInviteUsers.filter(user => {    
    return user.group_id === configurableRole?.group_id && user.email.toLocaleLowerCase().includes(debouncedText.toLocaleLowerCase());
  });

  const markText = (text: string, markerRef: React.RefObject<HTMLDivElement>, timeout?: number) => {
    const markInstance = new Mark(markerRef.current as HTMLElement);

    setTimeout(() => {
      markInstance.unmark({
        className: classes.mark,
        done: () => {
          markInstance.mark(text, {
            className: classes.mark,
          });
        },
      });
    }, timeout || 100);
  };

  React.useEffect(() => {
    if (markText) {
      const text = debouncedText || '';
      markText(text, markerRef);
    }
  }, [debouncedText]);

  const openUsersInvitingModal = () => {    
    localStorage.setItem('alreadySelectedGroup', configurableRole!.id);
    dispatch(setIsInviteUserOpen(true));
  };
  
  return (
    <div ref={markerRef} className={classes.memebersWrap}>
      <Input
        className={classes.searchInput}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder={t('Users.modals.configureRoles.searchMembers')}
        suffix={<SearchOutlined style={{color: 'gray', paddingRight: 4}}/>}
        allowClear
      />
      <Button
        icon={<Assets.MemberAddIcon />}
        className={classes.addBtn}
        onClick={openUsersInvitingModal}
      >
        {t('Documents.management.addUsers')}
      </Button>
      <div className={classes.memebersList}>
        {filteredUsers.map(user => {
          const isRoomOwnerCard = user?.id === dataRoom?.owner_id;
          let itemsDetails: MenuProps['items'] = [];

          if (!isRoomOwnerCard && isAvailableExcludeUser) {
            itemsDetails.push({
              key: 'delete',
              className: cn(classes.controlMenuItem, classes.warning),
              label: (
                <span className={classes.spanItem}>
                  <Assets.TrashIcon /> {t('Users.HelperMenu.delete')}
                </span>
              ),
              onClick: (e) => {
                e.domEvent.stopPropagation();
                dispatch(setUserChoosen(user || null));
                dispatch(setIsConfirmDeleteOpen(true));
              }
            });

            itemsDetails.push({
              type: 'divider',
            });
            itemsDetails.push({
              key: 'block',
              className: cn(classes.controlMenuItem, classes.warning),
              label: (
                <span className={classes.spanItem}>
                  <Assets.LockIcon /> { user.is_blocked ? t('Users.HelperMenu.unblock') : t('Users.HelperMenu.block') }
                </span>
              ),
              onClick: (e) => {
                e.domEvent.stopPropagation();
                dispatch(setUserChoosen(user || null));
                dispatch(setIsConfirmBlockOpen(true));
              }
            });
          };
          
          return (
            <div key={user.id} className={classes.rowWrap}>
              <Avatar size={44} className={classes.avatar}>
                <span className={classes.avatarWord}>{user.first_name[0]}{user.last_name[0]}</span>
              </Avatar>
              
              <div className={classes.infoSection}>
                <div>
                  <div>
                    {isRoomOwnerCard && (
                      <Tooltip title={t('Users.table.owner')}>
                        <Tag variant='outlined' className={classes.tagStatus}>{t('Rooms.badges.own')}</Tag>
                      </Tooltip>
                    )}
                    <span>{`${user.first_name} ${user.last_name}`}</span>
                    {user.is_blocked && <Tooltip className={classes.block} title={t('Users.table.userBlocked')}>
                      <span><Assets.LockIcon /></span>
                    </Tooltip>}
                  </div>
                  <div className={classes.inviteColor}>{user.email}</div>
                </div>
                {itemsDetails[0] && <Dropdown
                  menu={{ items: itemsDetails }}
                  placement='bottomRight'
                  classNames={{ root: classes.controlMenu }}
                >
                  { userWithIdProcessed.includes(user.id)
                    ? <Spinner size={24} width={'32px'}/>
                    : (
                      <Button
                        className={classes.detailIconBtn}
                        size='small'
                        type='text'
                        onClick={(e) => e.stopPropagation()}
                        icon={<Assets.VerticalIcon />}
                      />
                    )
                  }
                </Dropdown>}

              </div>
            </div>
          )
        })}
        {filteredInviteUsers.map(user => {
          let itemsDetails: MenuProps['items'] = [];

          if (user.invite_status !== 3 && isAvailableExcludeUser) {
            itemsDetails.push({
              key: 'delete_invite',
              className: classes.controlMenuItem,
              label: (
                <span className={classes.spanItem}>
                  <Assets.TrashIcon /> {t('Users.Tabs.actions.cancel')}
                </span>
                ),
              onClick: async (e) => {
                e.domEvent.stopPropagation();
                try {
                  dispatch(setProcessedUsers([...userWithIdProcessed, user.id!]));
                  await dispatch(deleteInvite({invite_id: user.id, t}));
                } catch { } finally {
                  dispatch(setProcessedUsers(userWithIdProcessed.filter(id => id !== user.id)));
                }
              },
            });

            if ((user.is_expired || user.invite_status === 2) && isAvailableInviteUser) {

              itemsDetails.push({
                type: 'divider',
                className: classes.divider,
              });
              itemsDetails.push({
                key: 'reinvite',
                className: classes.controlMenuItem,
                label: (
                  <span className={classes.spanItem}>
                    <ReloadOutlined style={{transform: 'rotateY(180deg)', padding: '0 3px'}}/> {t('Users.Tabs.actions.reinvite')}
                  </span>
                ),
                onClick: async (e) => {
                  e.domEvent.stopPropagation();
                  try {
                    dispatch(setProcessedUsers([...userWithIdProcessed, user.id!]));
                    await dispatch(reInviteHandle({currentUser: user, t}));
                  } catch { } finally {
                    dispatch(setProcessedUsers(userWithIdProcessed.filter(id => id !== user.id)));
                  }
                }
              });
            }
          }

          return (
            <div key={user.id} className={classes.rowWrap}>
              <Avatar size={44} className={classes.avatar}>
                <span className={classes.avatarWord}>{user.email[0]}</span>
              </Avatar>
              <div className={classes.infoSection}>
                <div>
                  <div className={classes.inviteColor}>{user.email}</div>
                  <div className={classes.inviteColor}>{t('Users.table.statuses.awaiting')}</div>
                </div>
                {itemsDetails[0] &&  <Dropdown
                  menu={{ items: itemsDetails }}
                  placement='bottomRight'
                  classNames={{ root: classes.controlMenu }}
                >
                  { userWithIdProcessed.includes(user.id)
                    ? <Spinner size={24} width={'32px'}/>
                    : (
                      <Button
                        className={classes.detailIconBtn}
                        size='small'
                        type='text'
                        onClick={(e) => e.stopPropagation()}
                        icon={<Assets.VerticalIcon />}
                      />
                    )
                  }
                </Dropdown>}
              </div>
            </div>
          )
        })}

        {!filteredInviteUsers[0] && !filteredUsers[0] && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('Users.Tabs.noUsers')}
          />
        )}
      </div>

      {isConfirmDeleteOpen && (
        <ConfirmDeleteUser
          isOpen={isConfirmDeleteOpen}
          onCancel={() => dispatch(setIsConfirmDeleteOpen(false))}
          submit={() => dispatch(deleteUserHandle({t}))}
        />
      )}

      {isConfirmBlockOpen && (
        <ConfirmChangeStatus
          isOpen={isConfirmBlockOpen}
          onCancel={() => dispatch(setIsConfirmBlockOpen(false))}
          changeBlockStatusUser={() => dispatch(changeBlockStatus({t}))}
        />
      )}
    </div>
  );
}

export default MembersOfRole;
